import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ManagerService} from '../../store/manager/+state/manager.service';
import {lastValueFrom} from 'rxjs';
import {formatDate} from '@angular/common';

@Component({
	selector: 'app-busy-mode',
	templateUrl: './busy-mode.component.html',
	styleUrl: './busy-mode.component.scss'
})
export class BusyModeComponent implements OnInit {
	readonly form = new FormGroup({
		busyModeUntil: new FormControl<string | null>(null, [Validators.required]),
		delayDuration: new FormControl<number | null>(null, [Validators.required])
	});
	busyOptions: string[] = [];
	delayOptions: number[] = Array.from({length: 30}, (_, i) => i + 1);
	maxPreparationDelay;

	constructor(
		public dialogRef: MatDialogRef<BusyModeComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			busyMode: {active: boolean; activeUntil: string; delayDuration: string};
			venue: string;
		},
		private managerSvc: ManagerService
	) {}

	ngOnInit(): void {
		this.form.controls.busyModeUntil.setValue(
			this.data.busyMode.activeUntil
				? formatDate(this.data.busyMode.activeUntil, 'HH:mm', 'en')
				: null
		);
		this.form.controls.delayDuration.setValue(
			+this.data.busyMode.delayDuration / 60
		);

		if (this.data.busyMode.active) this.form.disable();

		this.managerSvc.maxUberPreparationDelay(this.data.venue).subscribe(res => {
			this.maxPreparationDelay = res.maxPreparationDelay;
			this.busyOptions = this.generateOptions(this.maxPreparationDelay);
		});
	}

	private generateOptions(
		maxMinutes: number,
		startTime: Date = new Date()
	): string[] {
		const rounded = new Date(startTime);
		rounded.setSeconds(0, 0);

		const remainder = rounded.getMinutes() % 5;
		if (remainder !== 0) {
			rounded.setMinutes(rounded.getMinutes() + (5 - remainder));
		}

		const result: string[] = [];

		for (let i = 0; i <= Math.floor(maxMinutes / 5) * 5; i += 5) {
			const date = new Date(rounded.getTime() + i * 60_000);

			result.push(
				date.toLocaleTimeString('en-GB', {
					hour: '2-digit',
					minute: '2-digit',
					hour12: false
				})
			);
		}

		const lastDate = new Date(startTime.getTime() + maxMinutes * 60_000);
		result[result.length - 1] = lastDate.toLocaleTimeString('en-GB', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false
		});

		return result;
	}

	async submit() {
		if (!this.data.busyMode.active && this.form.invalid) return;

		const body: {
			busyMode: string;
			busyModeUntil?: string;
			delayDuration?: number;
		} = {
			busyMode: 'inactive'
		};

		if (!this.data.busyMode.active) {
			const {busyModeUntil, delayDuration} = this.form.getRawValue();

			const [untilHours, untilMinutes] = busyModeUntil!.split(':').map(Number);

			const activeUntil = new Date();
			activeUntil.setHours(untilHours, untilMinutes, 0, 0);

			body.busyMode = 'active';
			body.busyModeUntil = activeUntil.toISOString();
			body.delayDuration = delayDuration! * 60;
		}

		await lastValueFrom(this.managerSvc.setUberBusyMode(this.data.venue, body));

		this.dialogRef.close(true);
	}
}
